/**
 * client/src/lib/guardianships-api.ts
 *
 * The caller's own guardianship inbox — every connection where THIS user is the
 * guardian for someone else, in any lifecycle state. `status === "pending"` is a
 * request awaiting this person's answer, which is why the read is lifted out of
 * `pages/guardian-setup/index.tsx` (where it was an inline `apiRequest`) and
 * given a shared home: the Invitations page and the pending-invitation counts
 * both need it, and three inline copies of one URL is how a rename silently
 * un-wires a badge.
 *
 * Deliberately the SAME query key the Guardian Setup page already uses, so the
 * two surfaces share one react-query cache entry: answering a request on either
 * one updates the other and the nav badge together, with no second fetch.
 */

import { apiRequest } from "@/lib/queryClient";

import type { GuardianshipsResponse } from "@/pages/guardian-setup/types";

export function getGuardianships(): Promise<GuardianshipsResponse> {
  return apiRequest("/api/circle/guardianships") as Promise<GuardianshipsResponse>;
}

/**
 * Pending rows only — the ones that are actually an unanswered question.
 * `active` and `revoked` guardianships are relationships, not invitations, and
 * must never reach a badge that means "something is waiting on you".
 */
export function countPendingGuardianships(data: GuardianshipsResponse | undefined): number {
  return (data?.guardianships ?? []).filter((g) => g.status === "pending").length;
}
